'use client'

import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { MButton } from '@/_components/medante/ui/m-button'
import { pushPathWithHash } from '@/_lib/next-link-href'
import { cn, scrollToElementById } from '@/_lib/utils'

import AJobsHeroCard from './a-jobs-hero-card'
import AJobsHeroSwiper from './a-jobs-hero-swiper'
import ZJobsEyebrow from './z-jobs-eyebrow'

type TJobsHeroWaveAnchor = 'center' | 'twoThirds'

const AJobsHeroImage = ({
	src,
	alt,
	className,
	waveAnchor
}: {
	src: string
	alt: string
	className?: string
	/** Vlna od spodu fotky: desktop od stredu, mobile od 2/3 zľava (pravý okraj SVG na tej čiare). */
	waveAnchor?: TJobsHeroWaveAnchor
}) => {
	return (
		<div
			className={cn(
				'relative w-full max-w-[460px] lg:max-w-none',
				waveAnchor && 'overflow-x-visible overflow-y-visible',
				className
			)}
		>
			<div
				className={cn(
					'relative min-h-[268px] w-full overflow-hidden rounded-3xl lg:min-h-[500px] h-full'
				)}
			>
				<Image
					src={src}
					alt={alt}
					className='absolute inset-0 z-0 size-full object-cover'
					width={460}
					height={500}
					unoptimized={true}
				/>
			</div>
			{waveAnchor && (
				<img
					src={`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/lets-make-world-healthier/medante-wave-purple.png`}
					alt=''
					width={675}
					height={100}
					aria-hidden
					className={cn(
						'pointer-events-none absolute -bottom-16 lg:-bottom-14 z-1 h-[100px] w-full shrink-0 select-none object-cover lg:object-contain object-right',
						waveAnchor === 'center' ? 'right-[54%]' : 'right-[25%]'
					)}
				/>
			)}
		</div>
	)
}

const AJobsHero = () => {
	const t = useTranslations('pages.letsMakeWorldHealthier.hero')

	const router = useRouter()

	return (
		<header className='wrapper2 overflow-x-visible pt-8 pb-10 lg:pt-12 lg:pb-32' role='banner'>
			<div className='grid grid-cols-1 gap-8 overflow-x-visible lg:grid-cols-2 lg:items-stretch lg:gap-10'>
				<AJobsHeroImage
					src={`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/hero/michal-adndrejco-a-team.webp`}
					alt={t('heroImageAlt')}
					className='hidden lg:block'
					waveAnchor='center'
				/>

				<div className='flex flex-col justify-start'>
					<ZJobsEyebrow>{t('eyebrow')}</ZJobsEyebrow>
					<h1 className='type-medante-h1'>
						{t.rich('title', {
							br: () => <br />
						})}
					</h1>
					<div className='mt-4 lg:mt-6 text-base font-light max-w-lg'>
						<p>{t('paragraph1')}</p>
						<p className='mt-2'>{t('paragraph2')}</p>
					</div>
					<div className='flex flex-wrap gap-3 lg:gap-4 mt-6 lg:mt-8 justify-start'>
						<MButton
							variant='purple'
							onClick={() => {
								pushPathWithHash(router, '/kariera#pozicie')
							}}
						>
							{t('openPositions')}
						</MButton>
						<MButton
							variant='sand'
							onClick={() => {
								scrollToElementById('cim-zijeme')
							}}
						>
							{t('whatWeLive')}
						</MButton>
					</div>

					<AJobsHeroImage
						src={`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/hero/michal-adndrejco-a-team.webp`}
						alt={t('heroImageAlt')}
						className='block lg:hidden mt-6'
						waveAnchor='twoThirds'
					/>

					<div className='grid grid-cols-1 lg:grid-cols-3 gap-4 mt-20 lg:mt-8 max-w-[460px] lg:max-w-none'>
						<AJobsHeroSwiper className='lg:col-span-1' />

						<AJobsHeroCard
							title={t('swiperHolistic.title')}
							className='bg-m-purple-1 text-white lg:col-span-2 lg:max-w-[315px]'
						/>
					</div>
				</div>
			</div>
		</header>
	)
}

export default AJobsHero
